import React from 'react';
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import PropTypes from 'prop-types';
import { useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, useMultipleInputValues, variantProp, viewProps, contentfulProps } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps, contentfulProps]);
function selectBorderStyles(tokens) {
  return {
    borderBottomWidth: tokens.borderWidth,
    borderBottomStyle: tokens.borderStyle,
    borderBottomColor: tokens.borderColor
  };
}

/**
 * Flexible base component for lists where some or all items are collapsible headers.
 *
 * Pass a render function as `children` which takes and passes `openIds` and `onToggle` props to
 * <ExpandCollapse.Panel> children, and assign the panels explicit `panelId` props. The panels may be
 * nested (they do not need to be direct children), and non-interactive items may be included too.
 */
const ExpandCollapse = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children,
    tokens,
    variant,
    maxOpen,
    open,
    initialOpen,
    onChange,
    dataSet,
    ...rest
  } = _ref;
  const {
    currentValues: openIds,
    toggleOneValue: onToggle,
    resetValues,
    setValues,
    unsetValues
  } = useMultipleInputValues({
    maxValues: maxOpen,
    values: open,
    initialValues: initialOpen,
    onChange
  });
  const themeTokens = useThemeTokens('ExpandCollapse', tokens, variant);
  return /*#__PURE__*/_jsx(View, {
    style: staticStyles.container,
    ref: ref,
    ...selectProps(rest),
    dataSet: dataSet,
    children: /*#__PURE__*/_jsx(View, {
      style: selectBorderStyles(themeTokens),
      children: typeof children === 'function' ? children({
        openIds,
        onToggle,
        resetValues,
        setValues,
        unsetValues
      }) : children
    })
  });
});
ExpandCollapse.displayName = 'ExpandCollapse';
ExpandCollapse.propTypes = {
  ...selectedSystemPropTypes,
  variant: variantProp.propType,
  tokens: getTokensPropType('ExpandCollapse'),
  /**
   * ExpandCollapse usually takes a render function to which it passes an object with properties
   * { openIds, onToggle, resetValues, setValues, unsetValues }. openIds (array of ids) and
   * onToggle (function) will usually be passed to `<ExpandCollapse.Panel>`
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  /**
   * If set, enforces a specific maximum number of open items, closing already-open items when new
   * items are opened. By default, any number of items may be open at once.
   */
  maxOpen: PropTypes.number,
  /**
   * If passed, the item or items that are expanded will be controlled by a parent component
   */
  open: PropTypes.arrayOf(PropTypes.string),
  /**
   * Function called when which panels are currently open changes. Necessary if `open` is passed.
   */
  onChange: PropTypes.func,
  /**
   * Allows items with specified ids to be open by default on first load. Should not be used with `open`.
   */
  initialOpen: PropTypes.arrayOf(PropTypes.string)
};
export default ExpandCollapse;
const staticStyles = StyleSheet.create({
  container: {
    display: 'flex'
  }
});